import { useState, useEffect } from 'react';
import { ShoppingCart, ChevronDown, Menu, X, ArrowRight, Check, Droplet, Shield, Clock, Heart, User, Mail, Phone, MapPin } from 'lucide-react';

export default function Footer() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Handle scroll effect on navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Product data
  const products = [
    {
      id: 1,
      name: "Whole House Filtration System",
      price: "$1,999",
      image: "/api/placeholder/400/250",
      rating: 5,
      reviewCount: 124
    },
    {
      id: 2,
      name: "Under-Sink Advanced Filter",
      price: "$499",
      image: "/api/placeholder/400/250",
      rating: 4.8,
      reviewCount: 89
    },
    {
      id: 3,
      name: "Shower Filter System",
      price: "$129",
      image: "/api/placeholder/400/250",
      rating: 4.9,
      reviewCount: 216
    }
  ];

  // Testimonial data
  const testimonials = [
    {
      id: 1,
      text: "The difference in water quality is incredible. My skin has never felt better, and our appliances are free from limescale buildup for the first time!",
      name: "Sarah Johnson",
      location: "Brisbane",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    },
    {
      id: 2,
      text: "Installation was straightforward, and the customer support team was exceptional. They guided me through every step, making the process hassle-free.",
      name: "Michael Chen",
      location: "Sydney",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    },
    {
      id: 3,
      text: "Our water tastes incredible now! We've eliminated plastic bottle waste and are saving money while enjoying healthier water. Highly recommended!",
      name: "Lisa Anderson",
      location: "Melbourne",
      rating: 5,
      avatar: "/api/placeholder/64/64"
    }
  ];

  // Features data
  const features = [
    {
      id: 1,
      title: "Advanced Filtration Technology",
      description: "Our proprietary multi-stage filtration system removes 99.9% of contaminants including chlorine, heavy metals, and microplastics.",
      icon: <Droplet size={24} />,
      color: "bg-blue-500"
    },
    {
      id: 2,
      title: "Superior Longevity",
      description: "Designed to last 2-3 times longer than competing products, providing better value and reduced environmental impact.",
      icon: <Clock size={24} />,
      color: "bg-green-500"
    },
    {
      id: 3,
      title: "Health Protection",
      description: "Removes harmful contaminants while preserving beneficial minerals, resulting in healthier, better tasting water.",
      icon: <Shield size={24} />,
      color: "bg-purple-500"
    },
    {
      id: 4,
      title: "Customer Satisfaction",
      description: "With over 50,000 happy customers across Australia, our track record speaks for itself.",
      icon: <Heart size={24} />,
      color: "bg-pink-500"
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "How often do I need to replace the filters?",
      answer: "Our primary filters typically last 12-18 months, depending on your water usage and local water quality. The system includes an indicator that will let you know when it's time for a replacement."
    },
    {
      question: "Is installation included with purchase?",
      answer: "Yes, professional installation is included with all whole-house systems. For under-sink models, we provide detailed DIY installation guides, but professional installation is available for an additional fee."
    },
    {
      question: "Will a water filtration system affect my water pressure?",
      answer: "Our systems are designed to minimize pressure drop. Most customers experience no noticeable difference in water pressure after installation."
    },
    {
      question: "Are your filters certified?",
      answer: "Yes, all our filtration systems are independently tested and certified to meet or exceed Australian water quality standards."
    }
  ];

  // Water quality stats
  const waterStats = [
    { label: "Chemicals in tap water", value: "up to 50+" },
    { label: "Chlorine removal", value: "99.9%" },
    { label: "Heavy metals filtered", value: "99.8%" },
    { label: "Microplastics removed", value: "99.9%" }
  ];

  // Counter for animation
  const [count, setCount] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setCount(prevCount => (prevCount + 1) % testimonials.length);
    }, 5000);
    return () => clearTimeout(timer);
  }, [count, testimonials.length]);

  return (
      <footer className="bg-gray-900 text-white py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
              <div>
                <div className="flex items-center mb-6">
                  <div className="bg-blue-500 rounded-full p-2 mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">Home Filtaration System</h1>
                    <p className="text-xs text-gray-400">Pure, clean water for you</p>
                  </div>
                </div>
                <p className="text-gray-400 mb-6">
                  Providing advanced water filtration solutions to Australian homes since 2010.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-400 hover:text-white transition-all">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-all">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-all">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-bold mb-6">Quick Links</h3>
                <ul className="space-y-3">
                  <li><a href="/" className="text-gray-400 hover:text-white transition-all">Home</a></li>
                  <li><a href="/about" className="text-gray-400 hover:text-white transition-all">About Us</a></li>
                  <li><a href="/water-filtaration" className="text-gray-400 hover:text-white transition-all">Products</a></li>
                  <li><a href="/contact" className="text-gray-400 hover:text-white transition-all">Contact</a></li>
                 
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-bold mb-6">Our Products</h3>
                <ul className="space-y-3">
                  <li><a href="/water-filataration" className="text-gray-400 hover:text-white transition-all">Whole House Systems</a></li>
                  {/* <li><a href="#" className="text-gray-400 hover:text-white transition-all">Under-Sink Filters</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-all">Shower Filters</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-all">Countertop Filters</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-all">Replacement Filters</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-all">Accessories</a></li> */}
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-bold mb-6">Contact Us</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <MapPin size={18} className="text-blue-400 mt-1 mr-3" />
                    <span className="text-gray-400">Australia</span>
                  </li>
                  {/* <li className="flex items-center">
                    <Mail size={18} className="text-blue-400 mr-3" />
                    <span className="text-gray-400">info@homewaterfiltration.com.au</span>
                  </li>
                  <li className="flex items-center">
                    <Phone size={18} className="text-blue-400 mr-3" />
                    <span className="text-gray-400">1300 123 456</span>
                  </li> */}
                  <li className="flex items-center">
                    <User size={18} className="text-blue-400 mr-3" />
                    <span className="text-gray-400">Mon-Fri: 9am-5pm</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500 text-sm mb-4 md:mb-0">
                © 2023 Home Filtaration System. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-gray-500 hover:text-white text-sm transition-all">Privacy Policy</a>
                <a href="#" className="text-gray-500 hover:text-white text-sm transition-all">Terms of Service</a>
                <a href="#" className="text-gray-500 hover:text-white text-sm transition-all">Shipping Policy</a>
                <a href="#" className="text-gray-500 hover:text-white text-sm transition-all">Returns Policy</a>
              </div>
            </div>
          </div>
        </footer>
   
  );
}